import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Divider, Accordion, AccordionSummary, AccordionDetails, Select,
  MenuItem as MuiMenuItem, FormControl, InputLabel, Snackbar,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, IconButton,
} from '@mui/material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
import { localDateString } from '../utils/date';
import { elUpperCase } from '../utils/text';
const GREEN = '#10b981';
const RED = '#ef4444';
const ORANGE = '#f97316';

const today = localDateString();

function StatCard({ label, value, color }) {
  return (
    <Box sx={{ textAlign: 'center', p: 1.5, background: '#f8fafc', borderRadius: 1.5, minWidth: 80 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.3rem', color: color || NAV_BG }}>
        {value ?? '—'}
      </Typography>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
        {label}
      </Typography>
    </Box>
  );
}

function ChildReport({ child, authTokens, backendUrl, t }) {
  const [period, setPeriod] = useState('30d');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  useEffect(() => {
    if (!child) return;
    const end = new Date();
    const start = new Date();
    if (period === '7d') start.setDate(end.getDate() - 7);
    else if (period === '30d') start.setDate(end.getDate() - 30);
    else if (period === '3m') start.setMonth(end.getMonth() - 3);
    else if (period === '6m') start.setMonth(end.getMonth() - 6);
    else if (period === '1y') start.setFullYear(end.getFullYear() - 1);
    const fmt = localDateString;
    setLoading(true);
    fetch(backendUrl + `api/reports/child/${child.child_id}/?start=${fmt(start)}&end=${fmt(end)}`, { headers: authHeader })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.days) {
          const days = data.days;
          const total = days.length;
          const presentDays = days.filter(d => d.arrived).length;
          const absentDays = days.filter(d => d.absent).length;
          const arrivals = days.filter(d => d.arrival_time).map(d => d.arrival_time);
          const departures = days.filter(d => d.departure_time).map(d => d.departure_time);
          const avgTime = times => {
            if (!times.length) return null;
            const mins = times.map(t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; });
            const avg = Math.round(mins.reduce((a, b) => a + b, 0) / mins.length);
            return `${String(Math.floor(avg / 60)).padStart(2, '0')}:${String(avg % 60).padStart(2, '0')}`;
          };
          data = {
            present_pct: total > 0 ? Math.round((presentDays / total) * 100) : 0,
            absent_count: absentDays,
            avg_arrival: avgTime(arrivals),
            avg_departure: avgTime(departures),
            breakfast_count: days.filter(d => d.breakfast).length,
            lunch_count: days.filter(d => d.lunch).length,
          };
        }
        setReport(data); setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [child, period]);

  return (
    <Box>
      <FormControl size="small" sx={{ mb: 2 }}>
        <Select value={period} onChange={e => setPeriod(e.target.value)} sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: 1.5 }}>
          <MuiMenuItem value="7d">{t('parent_children_page.period_7d')}</MuiMenuItem>
          <MuiMenuItem value="30d">{t('parent_children_page.period_30d')}</MuiMenuItem>
          <MuiMenuItem value="3m">{t('parent_children_page.period_3m')}</MuiMenuItem>
          <MuiMenuItem value="6m">{t('parent_children_page.period_6m')}</MuiMenuItem>
          <MuiMenuItem value="1y">{t('parent_children_page.period_1y')}</MuiMenuItem>
        </Select>
      </FormControl>
      {loading && <CircularProgress size={18} sx={{ color: AMBER, display: 'block', mx: 'auto' }} />}
      {report && !loading && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <StatCard label={t('parent_children_page.stat_present_pct')} value={report.present_pct !== undefined ? `${report.present_pct}%` : null} color={GREEN} />
          <StatCard label={t('parent_children_page.stat_absences')} value={report.absent_count} color={RED} />
          <StatCard label={t('parent_children_page.stat_avg_arrival')} value={report.avg_arrival} />
          <StatCard label={t('parent_children_page.stat_avg_departure')} value={report.avg_departure} />
          <StatCard label={t('parent_children_page.stat_breakfasts')} value={report.breakfast_count} />
          <StatCard label={t('parent_children_page.stat_lunches')} value={report.lunch_count} />
        </Box>
      )}
    </Box>
  );
}

export default function ParentChildrenPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();

  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [absencesByChild, setAbsencesByChild] = useState({});

  // Dialog state
  const [absenceDialog, setAbsenceDialog] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [absenceFrom, setAbsenceFrom] = useState(today);
  const [absenceTo, setAbsenceTo] = useState(today);
  const [absenceReason, setAbsenceReason] = useState('');
  const [absenceType, setAbsenceType] = useState('full');
  const [skipTo, setSkipTo] = useState(true);
  const [skipFrom, setSkipFrom] = useState(true);
  const [dialogAbsences, setDialogAbsences] = useState([]);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState(null);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  const fetchAbsencesForChild = useCallback(async (childId) => {
    const res = await fetch(backendUrl + `api/parent/absences/?child_id=${childId}`, { headers: authHeader });
    if (res.ok) return res.json();
    return [];
  }, [authTokens, backendUrl]);

  useEffect(() => {
    fetch(backendUrl + 'api/parent/children/', { headers: authHeader })
      .then(r => r.ok ? r.json() : [])
      .then(async (data) => {
        const list = Array.isArray(data) ? data : [];
        setChildren(list);
        // Fetch absences for all children to show today's status
        const map = {};
        await Promise.all(list.map(async (child) => {
          map[child.child_id] = await fetchAbsencesForChild(child.child_id);
        }));
        setAbsencesByChild(map);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getTodayAbsence = (childId) => {
    const absences = absencesByChild[childId] || [];
    return absences.find(a => a.date === today) || null;
  };

  const openAbsenceDialog = async (child) => {
    setSelectedChild(child);
    setAbsenceFrom(today);
    setAbsenceTo(today);
    setAbsenceReason('');
    setAbsenceType('full');
    setSkipTo(true);
    setSkipFrom(true);
    setDialogAbsences(absencesByChild[child.child_id] || []);
    setAbsenceDialog(true);
    setLoadingAbsences(true);
    const fresh = await fetchAbsencesForChild(child.child_id);
    setDialogAbsences(fresh);
    setAbsencesByChild(prev => ({ ...prev, [child.child_id]: fresh }));
    setLoadingAbsences(false);
  };

  const handleReportAbsence = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(backendUrl + 'api/parent/absence/', {
        method: 'POST', headers: authHeader,
        body: JSON.stringify({
          child_id: selectedChild.child_id,
          start_date: absenceFrom,
          end_date: absenceTo,
          reason: absenceReason,
          absence_type: absenceType === 'full' ? 0 : 1,
          skip_to_school: absenceType === 'bus' ? skipTo : true,
          skip_from_school: absenceType === 'bus' ? skipFrom : true,
        }),
      });
      if (res.ok) {
        setAbsenceFrom(today);
        setAbsenceTo(today);
        setAbsenceReason('');
        const fresh = await fetchAbsencesForChild(selectedChild.child_id);
        setDialogAbsences(fresh);
        setAbsencesByChild(prev => ({ ...prev, [selectedChild.child_id]: fresh }));
        setSnack({ severity: 'success', msg: t('parent_children_page.absence_saved') });
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ severity: 'error', msg: data.error || t('parent_children_page.absence_failed') });
      }
    } catch {
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setSubmitting(false);
  };

  const handleDeleteAbsence = async (absence) => {
    setDeletingId(absence.absence_id);
    try {
      const res = await fetch(backendUrl + 'api/parent/absence/cancel/', {
        method: 'DELETE', headers: authHeader,
        body: JSON.stringify({ child_id: selectedChild.child_id, date: absence.date }),
      });
      if (res.ok) {
        const updated = dialogAbsences.filter(a => a.absence_id !== absence.absence_id);
        setDialogAbsences(updated);
        setAbsencesByChild(prev => ({ ...prev, [selectedChild.child_id]: updated }));
        window.dispatchEvent(new CustomEvent('absence-changed'));
        setSnack({ severity: 'success', msg: t('parent_children_page.absence_deleted') });
      } else {
        setSnack({ severity: 'error', msg: t('parent_children_page.absence_failed') });
      }
    } catch {
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setDeletingId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: AMBER }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.4rem', color: NAV_BG, mb: 3 }}>
        {t('nav.parent_children')}
      </Typography>

      {children.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>{t('parent_children_page.no_children')}</Alert>
      )}

      {children.map((child) => {
        const todayAbsence = getTodayAbsence(child.child_id);
        const isFull = todayAbsence?.absence_type === 0;
        const isBusAbsent = todayAbsence?.absence_type === 1;

        return (
          <Paper key={child.child_id} sx={{ mb: 2.5, borderRadius: 2, border: `1px solid ${isFull ? 'rgba(239,68,68,0.3)' : isBusAbsent ? 'rgba(249,115,22,0.3)' : '#e2e8f0'}`, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ px: 2.5, py: 2, background: isFull ? 'linear-gradient(135deg,#7f1d1d,#991b1b)' : isBusAbsent ? 'linear-gradient(135deg,#7c2d12,#9a3412)' : NAV_BG, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: '50%',
                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '0.9rem',
              }}>
                {child.firstname?.[0]}{child.lastname?.[0]}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: 'white' }}>
                  {child.firstname} {child.lastname}
                </Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
                  {[child.schoolclass_name, child.department_name].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                {/* Today's absence badge */}
                {isFull && (
                  <Chip
                    icon={<PersonOffIcon sx={{ fontSize: 12 }} />}
                    label={t('parent_children_page.absent_today')}
                    size="small"
                    sx={{ background: 'rgba(239,68,68,0.25)', color: '#fca5a5', fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', fontWeight: 700 }}
                  />
                )}
                {isBusAbsent && (
                  <Chip
                    icon={<DirectionsBusIcon sx={{ fontSize: 12 }} />}
                    label={t('parent_children_page.bus_absent_today')}
                    size="small"
                    sx={{ background: 'rgba(249,115,22,0.25)', color: '#fed7aa', fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', fontWeight: 700 }}
                  />
                )}
                {/* Bus route chips */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {child.bus_to_school && (
                    <Chip
                      size="small"
                      icon={<DirectionsBusIcon sx={{ fontSize: 12 }} />}
                      label="→"
                      sx={{
                        background: (isBusAbsent && todayAbsence?.skip_to_school) ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
                        color: (isBusAbsent && todayAbsence?.skip_to_school) ? '#fca5a5' : '#6ee7b7',
                        fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem',
                        '& .MuiChip-label': { textDecoration: (isBusAbsent && todayAbsence?.skip_to_school) ? 'line-through' : 'none' },
                      }}
                    />
                  )}
                  {child.bus_from_school && (
                    <Chip
                      size="small"
                      icon={<DirectionsBusIcon sx={{ fontSize: 12 }} />}
                      label="←"
                      sx={{
                        background: (isBusAbsent && todayAbsence?.skip_from_school) ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)',
                        color: (isBusAbsent && todayAbsence?.skip_from_school) ? '#fca5a5' : '#93c5fd',
                        fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem',
                        '& .MuiChip-label': { textDecoration: (isBusAbsent && todayAbsence?.skip_from_school) ? 'line-through' : 'none' },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 2.5, py: 2 }}>
              <Button
                variant="outlined"
                disableElevation
                startIcon={<EventBusyIcon sx={{ fontSize: 16 }} />}
                onClick={() => openAbsenceDialog(child)}
                sx={{
                  fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none',
                  borderRadius: 1.5, borderColor: '#e2e8f0', color: '#64748b',
                  fontSize: '0.85rem', mb: 2,
                  '&:hover': { borderColor: RED, color: RED, background: '#fff5f5' },
                }}
              >
                {t('parent_children_page.manage_absences')}
              </Button>

              <Accordion elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: '8px', minHeight: 44, '& .MuiAccordionSummary-content': { my: 1 } }}>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: NAV_BG }}>
                    {t('parent_children_page.attendance_history')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1 }}>
                  <ChildReport child={child} authTokens={authTokens} backendUrl={backendUrl} t={t} />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Paper>
        );
      })}

      {/* Absence management dialog */}
      <Dialog open={absenceDialog} onClose={() => setAbsenceDialog(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {selectedChild
            ? t('parent_children_page.manage_absences_for', { name: selectedChild.firstname })
            : t('parent_children_page.manage_absences')}
          <IconButton size="small" onClick={() => setAbsenceDialog(false)} sx={{ color: '#94a3b8', '&:hover': { color: NAV_BG } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Type toggle */}
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
            {elUpperCase(t('parent_children_page.type_label'))}
          </Typography>
          <ToggleButtonGroup
            value={absenceType} exclusive
            onChange={(_, v) => { if (v) setAbsenceType(v); }}
            size="small" fullWidth
            sx={{ mb: 2, '& .MuiToggleButton-root': { fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', textTransform: 'none', py: 0.75, borderColor: '#e2e8f0', '&.Mui-selected': { background: absenceType === 'full' ? 'rgba(239,68,68,0.1)' : 'rgba(249,115,22,0.1)', color: absenceType === 'full' ? RED : ORANGE, fontWeight: 700, borderColor: absenceType === 'full' ? RED : ORANGE } } }}
          >
            <ToggleButton value="full">{t('parent_children_page.type_full')}</ToggleButton>
            <ToggleButton value="bus">{t('parent_children_page.type_bus')}</ToggleButton>
          </ToggleButtonGroup>

          {/* Route checkboxes */}
          {absenceType === 'bus' && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                {elUpperCase(t('parent_children_page.routes_label'))}
              </Typography>
              {selectedChild?.bus_to_school && (
                <FormControlLabel
                  control={<Checkbox checked={skipTo} onChange={e => setSkipTo(e.target.checked)} size="small" sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                  label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem' }}>{t('parent_children_page.skip_to_school')}</Typography>}
                />
              )}
              {selectedChild?.bus_from_school && (
                <FormControlLabel
                  control={<Checkbox checked={skipFrom} onChange={e => setSkipFrom(e.target.checked)} size="small" sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                  label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem' }}>{t('parent_children_page.skip_from_school')}</Typography>}
                />
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth size="small" type="date" label={t('parent_children_page.from_label')}
              value={absenceFrom} onChange={e => setAbsenceFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, '&.Mui-focused fieldset': { borderColor: AMBER } }, '& label.Mui-focused': { color: AMBER } }}
            />
            <TextField
              fullWidth size="small" type="date" label={t('parent_children_page.to_label')}
              value={absenceTo} onChange={e => setAbsenceTo(e.target.value)}
              inputProps={{ min: absenceFrom }}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, '&.Mui-focused fieldset': { borderColor: AMBER } }, '& label.Mui-focused': { color: AMBER } }}
            />
            <TextField
              fullWidth size="small" label={t('parent_children_page.reason_optional')}
              value={absenceReason} onChange={e => setAbsenceReason(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, '&.Mui-focused fieldset': { borderColor: AMBER } }, '& label.Mui-focused': { color: AMBER } }}
            />
          </Box>

          {/* Scheduled absences list */}
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
            {elUpperCase(t('parent_children_page.scheduled'))}
          </Typography>
          {loadingAbsences ? (
            <CircularProgress size={18} sx={{ color: AMBER, display: 'block', mx: 'auto', my: 1 }} />
          ) : dialogAbsences.length === 0 ? (
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic' }}>
              {t('parent_children_page.no_scheduled')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 200, overflowY: 'auto' }}>
              {dialogAbsences.map(a => (
                <Box key={a.absence_id} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, p: '8px 10px', borderRadius: '10px',
                  bgcolor: a.absence_type === 0 ? '#fff5f5' : '#fff7ed',
                  border: `1px solid ${a.absence_type === 0 ? '#fecaca' : '#fed7aa'}`,
                }}>
                  <CalendarMonthIcon sx={{ fontSize: 14, color: a.absence_type === 0 ? RED : ORANGE, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: a.absence_type === 0 ? '#991b1b' : '#92400e' }}>
                      {a.date}{a.reason ? ` — ${a.reason}` : ''}
                    </Typography>
                    {a.created_by_school && (
                      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#94a3b8' }}>
                        {t('parent_children_page.by_school')}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={a.absence_type === 0 ? t('parent_children_page.chip_full') : t('parent_children_page.chip_bus')}
                    size="small"
                    sx={{ height: 16, fontSize: '0.62rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700, background: a.absence_type === 0 ? 'rgba(239,68,68,0.12)' : 'rgba(249,115,22,0.15)', color: a.absence_type === 0 ? RED : ORANGE }}
                  />
                  <IconButton size="small" onClick={() => handleDeleteAbsence(a)} disabled={deletingId === a.absence_id}
                    sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: RED } }}>
                    {deletingId === a.absence_id
                      ? <CircularProgress size={14} sx={{ color: RED }} />
                      : <DeleteIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAbsenceDialog(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained" disableElevation onClick={handleReportAbsence}
            disabled={submitting || !absenceFrom || !absenceTo || (absenceType === 'bus' && !skipTo && !skipFrom)}
            sx={{
              background: absenceType === 'full' ? RED : ORANGE, color: 'white',
              fontFamily: '"Outfit", sans-serif', fontWeight: 700,
              textTransform: 'none', borderRadius: 1.5,
              '&:hover': { background: absenceType === 'full' ? '#dc2626' : '#ea6c0a' },
            }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : t('parent_children_page.report_btn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ top: { xs: 'calc(8px + var(--sat))', sm: 'calc(24px + var(--sat))' } }}>
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
